import { z } from 'zod';

export const GapDisposition = z.enum([
  'blocking',
  'inferable',
  'worker_resolvable',
  'future_confirmable',
  'irrelevant',
]);
export const GapRiskClass = z.enum(['low', 'moderate', 'high', 'prohibited']);
export const GapResolutionOwner = z.enum(['owner', 'worker', 'system', 'none']);
export const EvaluateGapRequest = z.object({
  chain_planner_run_id: z.string().min(1).max(40).optional(),
  chain_family: z.enum(['pool_filter', 'restaurant_reservation', 'guest_preparation']),
  candidate_key: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  subject: z.enum([
    'money',
    'allergy',
    'child_safety',
    'access',
    'transport',
    'schedule',
    'preference',
    'quantity',
    'vendor',
    'wording',
    'irrelevant_detail',
  ]),
  proposed_gap_type: GapDisposition,
  proposed_risk_class: GapRiskClass,
  proposed_resolution_owner: GapResolutionOwner,
  uncertainty: z.number().min(0).max(1).optional(),
  evidence_refs: z
    .array(
      z.object({
        id: z.string().min(1).max(80),
        truth_label: z.enum([
          'confirmed_fact',
          'observed_pattern',
          'general_practice',
          'inference',
          'missing_information',
        ]),
      }),
    )
    .max(20)
    .default([]),
  untrusted_source_text: z.string().max(2000).optional(),
  requested_deadline_at: z.string().datetime({ offset: true }).optional(),
});
export type EvaluateGap = z.infer<typeof EvaluateGapRequest>;
export const CreateClarificationRequest = z.object({
  gap_decision_id: z.string().min(1).max(40),
  asked_to_user_id: z.string().min(1).max(40),
  wording: z.string().min(3).max(300),
  answer_kind: z.enum(['single_choice', 'short_text', 'voice_or_text']),
  choices: z
    .array(
      z.object({
        key: z.string().min(1).max(60),
        label: z.string().min(1).max(120),
        value: z.unknown(),
      }),
    )
    .max(8)
    .default([]),
  suppression_key: z.string().min(1).max(120).optional(),
  expires_at: z.string().datetime({ offset: true }),
});
export const AnswerClarificationRequest = z.object({
  expected_version: z.number().int().positive(),
  idempotency_key: z.string().min(8).max(80),
  answer: z.record(z.string(), z.unknown()),
  storage_choice: z.enum(['use_once', 'confirm_household_rule', 'do_not_save']),
  voice_transcript_ref: z.string().max(200).optional(),
});
export const ClarificationActionRequest = z.object({
  expected_version: z.number().int().positive(),
  action: z.enum(['defer', 'abandon', 'suppress']),
});
export type CreateClarification = z.infer<typeof CreateClarificationRequest>;
export type AnswerClarification = z.infer<typeof AnswerClarificationRequest>;
export type ClarificationAction = z.infer<typeof ClarificationActionRequest>;
